import React, { useEffect, useState, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import StartProjectModal from "@/components/site/StartProjectModal";
import ClapAI from "@/components/site/ClapAI";
import HomePage from "@/pages/HomePage";
import CapabilitiesPage from "@/pages/CapabilitiesPage";
import TalentPage from "@/pages/TalentPage";
import WorkPage from "@/pages/WorkPage";
import AboutPage from "@/pages/AboutPage";
import ContactPage from "@/pages/ContactPage";
import NotFoundPage from "@/pages/NotFoundPage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import AdminDashboardPage from "@/pages/AdminDashboardPage";

const ScrollToTop = () => {
    const { pathname } = useLocation();
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "auto" });
    }, [pathname]);
    return null;
};

const isAdminPath = (pathname) => pathname.startsWith("/admin");

// Slot helpers — each renders nothing on /admin* so the admin area stays clean.
const PublicNavbar = ({ onStartProject }) => {
    const { pathname } = useLocation();
    if (isAdminPath(pathname)) return null;
    return <Navbar onStartProject={onStartProject} />;
};

const PublicFooter = ({ onStartProject }) => {
    const { pathname } = useLocation();
    if (isAdminPath(pathname)) return null;
    return <Footer onStartProject={onStartProject} />;
};

const PublicOverlays = ({ openStartProject, setOpenStartProject }) => {
    const { pathname } = useLocation();
    if (isAdminPath(pathname)) return null;
    return (
        <>
            <StartProjectModal
                open={openStartProject}
                onOpenChange={setOpenStartProject}
            />
            <ClapAI />
        </>
    );
};

const AnimatedRoutes = ({ onStartProject }) => {
    const location = useLocation();
    return (
        <AnimatePresence mode="wait">
            <Routes location={location} key={location.pathname}>
                <Route path="/" element={<PageWrap><HomePage onStartProject={onStartProject} /></PageWrap>} />
                <Route path="/capabilities" element={<PageWrap><CapabilitiesPage onStartProject={onStartProject} /></PageWrap>} />
                <Route path="/talent" element={<PageWrap><TalentPage onStartProject={onStartProject} /></PageWrap>} />
                <Route path="/work" element={<PageWrap><WorkPage onStartProject={onStartProject} /></PageWrap>} />
                <Route path="/about" element={<PageWrap><AboutPage onStartProject={onStartProject} /></PageWrap>} />
                <Route path="/contact" element={<PageWrap><ContactPage onStartProject={onStartProject} /></PageWrap>} />
                <Route path="/admin" element={<AdminLoginPage />} />
                <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
                <Route path="*" element={<PageWrap><NotFoundPage onStartProject={onStartProject} /></PageWrap>} />
            </Routes>
        </AnimatePresence>
    );
};

const PageWrap = ({ children }) => (
    <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -4 }}
        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
        className="page-shell"
    >
        {children}
    </motion.div>
);

function App() {
    const [openStartProject, setOpenStartProject] = useState(false);
    const onStartProject = useCallback(() => setOpenStartProject(true), []);

    return (
        <HelmetProvider>
            <div className="App">
                <BrowserRouter>
                <ScrollToTop />
                <PublicNavbar onStartProject={onStartProject} />
                <main>
                    <AnimatedRoutes onStartProject={onStartProject} />
                </main>
                <PublicFooter onStartProject={onStartProject} />
                <PublicOverlays
                    openStartProject={openStartProject}
                    setOpenStartProject={setOpenStartProject}
                />
                <Toaster
                    position="top-center"
                    theme="light"
                    richColors
                    closeButton
                    toastOptions={{
                        style: {
                            background: "hsl(0 0% 100%)",
                            border: "1px solid rgba(20,30,50,0.10)",
                            color: "hsl(220 42% 11%)",
                        },
                    }}
                />
                </BrowserRouter>
            </div>
        </HelmetProvider>
    );
}

export default App;
